import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ReactComponent as PantherLogo } from '../assets/panther-logo.svg';

const OFFLINE_FLAG_KEY = 'panther:offline';
/** How long before the full-screen banner appears (ms). */
const OFFLINE_GRACE_MS = 60_000;
/** How often to silently probe during the grace period (ms). */
const PROBE_INTERVAL_MS = 8_000;
/** How often to auto-retry once the banner is visible (ms). */
const BANNER_RETRY_MS = 10_000;

/**
 * Lightweight connectivity probe — HEAD to /api/v1/health with a 5s timeout.
 * Returns true if the server answered (any status), false on network failure.
 */
async function probeConnectivity(): Promise<boolean> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 5000);
    await fetch('/api/v1/health', { method: 'HEAD', cache: 'no-store', signal: ctrl.signal });
    clearTimeout(t);
    return true;
  } catch {
    return false;
  }
}

/**
 * NetworkStatusBanner — always rendered OUTSIDE KeycloakProvider.
 *
 * Shows a full-screen offline overlay only after 60s of sustained disconnection.
 * During the grace period it silently probes every 8s — if connectivity returns
 * the debounce timer is cancelled and no banner is ever shown.
 * Once the banner IS shown it keeps auto-retrying every 10s and auto-dismisses
 * when connectivity is restored. The manual button is kept for impatient users.
 */
const NetworkStatusBanner: React.FC = () => {
  const [isOnline, _setIsOnline] = useState<boolean>(true);
  const [showRestored, setShowRestored] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const deployDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Countdown (seconds) shown on the banner before next auto-retry.
  const [nextRetryIn, setNextRetryIn] = useState(BANNER_RETRY_MS / 1000);
  const [isAutoRetrying, setIsAutoRetrying] = useState(false);

  const prevOnline = useRef(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmedOfflineThisSession = useRef(false);
  const retrying = useRef(false);
  const offlineFromKeycloak = useRef(false);
  const offlineDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Interval that silently probes during the grace period.
  const gracePeriodProbeInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const setIsOnline = useCallback((online: boolean) => {
    if (online) localStorage.removeItem(OFFLINE_FLAG_KEY);
    else localStorage.setItem(OFFLINE_FLAG_KEY, '1');
    _setIsOnline(online);
  }, []);

  const cancelOfflineDebounce = useCallback(() => {
    if (offlineDebounceTimer.current) {
      clearTimeout(offlineDebounceTimer.current);
      offlineDebounceTimer.current = null;
    }
    if (gracePeriodProbeInterval.current) {
      clearInterval(gracePeriodProbeInterval.current);
      gracePeriodProbeInterval.current = null;
    }
  }, []);

  // ── Background probe that runs DURING the grace period ──────────────────
  // If we detect connectivity again, cancel the debounce → banner never shows.
  const startGracePeriodProbing = useCallback(() => {
    if (gracePeriodProbeInterval.current) return; // already running
    gracePeriodProbeInterval.current = setInterval(async () => {
      const alive = navigator.onLine && await probeConnectivity();
      if (alive) {
        cancelOfflineDebounce();
        if (!offlineFromKeycloak.current) setIsOnline(true);
      }
    }, PROBE_INTERVAL_MS);
  }, [cancelOfflineDebounce, setIsOnline]);

  // ── Event handlers ───────────────────────────────────────────────────────
  useEffect(() => {
    if (localStorage.getItem(OFFLINE_FLAG_KEY)) _setIsOnline(false);

    const markOnline = () => {
      cancelOfflineDebounce();
      if (offlineFromKeycloak.current) return;
      setIsOnline(true);
    };

    const markOffline = () => {
      cancelOfflineDebounce();
      startGracePeriodProbing();
      offlineDebounceTimer.current = setTimeout(() => {
        confirmedOfflineThisSession.current = true;
        setIsOnline(false);
      }, OFFLINE_GRACE_MS);
    };

    const handleConnectivity = (e: Event) => {
      const online = (e as CustomEvent<{ online: boolean }>).detail.online;
      if (!online) {
        cancelOfflineDebounce();
        startGracePeriodProbing();
        offlineDebounceTimer.current = setTimeout(() => {
          confirmedOfflineThisSession.current = true;
          offlineFromKeycloak.current = true;
          setIsOnline(false);
        }, OFFLINE_GRACE_MS);
      } else {
        cancelOfflineDebounce();
        offlineFromKeycloak.current = false;
        setIsOnline(true);
      }
    };

    const handleSessionExpired = () => setSessionExpired(true);

    const handleDeploying = () => {
      if (deployDismissTimer.current) clearTimeout(deployDismissTimer.current);
      setIsDeploying(true);
      // Auto-dismiss after 90s even if no "ok" arrives
      deployDismissTimer.current = setTimeout(() => setIsDeploying(false), 90_000);
    };
    const handleDeployOk = () => {
      if (deployDismissTimer.current) clearTimeout(deployDismissTimer.current);
      // Dismiss after a brief pause so user sees the banner resolve
      deployDismissTimer.current = setTimeout(() => setIsDeploying(false), 1500);
    };

    window.addEventListener('online', markOnline);
    window.addEventListener('offline', markOffline);
    window.addEventListener('panther:connectivity', handleConnectivity);
    window.addEventListener('panther:session-expired', handleSessionExpired);
    window.addEventListener('panther:deploying', handleDeploying);
    window.addEventListener('panther:deploy-ok', handleDeployOk);
    return () => {
      window.removeEventListener('online', markOnline);
      window.removeEventListener('offline', markOffline);
      window.removeEventListener('panther:connectivity', handleConnectivity);
      window.removeEventListener('panther:session-expired', handleSessionExpired);
      window.removeEventListener('panther:deploying', handleDeploying);
      window.removeEventListener('panther:deploy-ok', handleDeployOk);
      cancelOfflineDebounce();
      if (deployDismissTimer.current) clearTimeout(deployDismissTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── "Conexão restaurada" toast ───────────────────────────────────────────
  useEffect(() => {
    if (!isOnline) {
      setShowRestored(false);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    } else if (!prevOnline.current && isOnline) {
      if (confirmedOfflineThisSession.current) {
        setShowRestored(true);
        hideTimer.current = setTimeout(() => setShowRestored(false), 3000);
      }
    }
    prevOnline.current = isOnline;
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
  }, [isOnline]);

  // ── Auto-retry loop while the banner is visible ──────────────────────────
  useEffect(() => {
    if (isOnline) return;

    setNextRetryIn(BANNER_RETRY_MS / 1000);

    // Countdown tick every second.
    const tick = setInterval(() => {
      setNextRetryIn(prev => (prev > 1 ? prev - 1 : prev));
    }, 1000);

    // Probe + trigger auth retry every BANNER_RETRY_MS.
    const doRetry = async () => {
      setIsAutoRetrying(true);
      const alive = navigator.onLine && await probeConnectivity();
      setIsAutoRetrying(false);
      if (alive) {
        if (!retrying.current) {
          retrying.current = true;
          window.dispatchEvent(new CustomEvent('panther:retry-auth'));
          setTimeout(() => { retrying.current = false; }, 4000);
        }
      } else {
        setNextRetryIn(BANNER_RETRY_MS / 1000);
      }
    };

    // First probe right away, then every BANNER_RETRY_MS.
    doRetry();
    const retryInterval = setInterval(doRetry, BANNER_RETRY_MS);

    return () => {
      clearInterval(tick);
      clearInterval(retryInterval);
    };
  }, [isOnline]);

  // ── Render ───────────────────────────────────────────────────────────────
  if (sessionExpired) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: '#0f0f1a',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 24,
        fontFamily: 'Montserrat, sans-serif',
      }}>
        <PantherLogo style={{ width: 72, height: 72, opacity: 0.9 } as React.CSSProperties} />
        <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: '0.05em' }}>
          ACCESS
        </h2>
        <div style={{ textAlign: 'center', lineHeight: 1.6 }}>
          <p style={{ color: '#e0a055', fontWeight: 600, fontSize: 15, margin: '0 0 6px' }}>
            Sessão expirada
          </p>
          <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
            Sua sessão expirou por inatividade. Faça login novamente.
          </p>
        </div>
        <button
          onClick={() => {
            if (retrying.current) return;
            retrying.current = true;
            setSessionExpired(false);
            window.dispatchEvent(new CustomEvent('panther:retry-auth'));
            setTimeout(() => { retrying.current = false; }, 4000);
          }}
          style={{
            marginTop: 8, padding: '10px 28px',
            background: '#96bf13', color: '#0f0f1a',
            border: 'none', borderRadius: 8,
            fontSize: 14, fontWeight: 700,
            fontFamily: 'Montserrat, sans-serif',
            cursor: 'pointer', letterSpacing: '0.02em',
          }}
        >
          Entrar novamente
        </button>
      </div>
    );
  }

  if (isOnline && !showRestored && !isDeploying) return null;

  // Non-blocking top banner when ECS deploy is in progress
  if (isOnline && isDeploying) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99998,
        background: '#c47c0a', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '7px 16px', fontSize: 13, fontWeight: 600,
        fontFamily: 'Montserrat, sans-serif',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}>
        <svg width="14" height="14" viewBox="0 0 16 16" style={{ animation: 'spin 1.2s linear infinite', flexShrink: 0 }}>
          <circle cx="8" cy="8" r="6" fill="none" stroke="#fff" strokeWidth="2" strokeDasharray="28" strokeDashoffset="10" />
        </svg>
        Sistema em atualização, aguarde um momento...
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (showRestored) {
    return (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 99999,
        background: '#2d7d46', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '10px 16px', fontSize: 13, fontWeight: 600,
        fontFamily: 'Montserrat, sans-serif',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.3)',
      }}>
        <span>●</span> Conexão restaurada
      </div>
    );
  }

  // Full-screen offline overlay.
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: '#0f0f1a',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 24,
      fontFamily: 'Montserrat, sans-serif',
    }}>
      <PantherLogo style={{ width: 72, height: 72, opacity: 0.9 } as React.CSSProperties} />
      <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: 0, letterSpacing: '0.05em' }}>
        ACCESS
      </h2>
      <div style={{ textAlign: 'center', lineHeight: 1.6 }}>
        <p style={{ color: '#e05555', fontWeight: 600, fontSize: 15, margin: '0 0 6px' }}>
          Sem conexão com a internet
        </p>
        <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
          Verificando conexão automaticamente...
        </p>
      </div>

      {/* Auto-retry status */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        {isAutoRetrying ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#96bf13', fontSize: 13 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: 'spin 1s linear infinite' }}>
              <circle cx="8" cy="8" r="6" fill="none" stroke="#96bf13" strokeWidth="2" strokeDasharray="28" strokeDashoffset="10" />
            </svg>
            Verificando...
          </div>
        ) : (
          <p style={{ color: '#555', fontSize: 12, margin: 0 }}>
            Próxima tentativa em {nextRetryIn}s
          </p>
        )}
      </div>

      <button
        onClick={() => {
          if (retrying.current) return;
          retrying.current = true;
          setNextRetryIn(BANNER_RETRY_MS / 1000);
          window.dispatchEvent(new CustomEvent('panther:retry-auth'));
          setTimeout(() => { retrying.current = false; }, 4000);
        }}
        style={{
          marginTop: 4, padding: '10px 28px',
          background: '#96bf13', color: '#0f0f1a',
          border: 'none', borderRadius: 8,
          fontSize: 14, fontWeight: 700,
          fontFamily: 'Montserrat, sans-serif',
          cursor: 'pointer', letterSpacing: '0.02em',
        }}
      >
        Tentar agora
      </button>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default NetworkStatusBanner;
